import React, { FC, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../redux/reducers";
import {
  Role,
  RolePermissionType,
  RolePermissionPayload,
  RoleMaster,
} from "../redux/actions/authUserActions";
import "../styles/RolePermission.css";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import { addRole } from "../redux/actions/roleAction";
import Modal from "../components/Modal";
import { getRoles } from "../services/RolesService";
import { AppDispatch } from "../redux/store";
import { savePermissions } from "../redux/actions/rolePermissionAction";
import { MENU_ITEMS as ADMIN_MENU_ITEMS } from "../components/custom/SideNavBarAdmin";
import { MENU_ITEMS as USER_MENU_ITEMS } from "../components/custom/SideNavBarUser";
import {
  getRolePermissions,
  saveRolePermissions,
  updateRolePermissions,
} from "../services/RolePermissionService";
import { Col, Row } from "react-bootstrap";

interface RolePermissionManagerProps {
  onSave?: (payload: RolePermissionPayload) => void;
  isLoading?: boolean;
}

const RolePermissionManager: FC<RolePermissionManagerProps> = ({
  onSave,
  isLoading = false,
}) => {
  const authUser = useSelector((state: StoreState) => state.authUser);

  const [selectedRole, setSelectedRole] = useState<string>("");
  const [roles, setRoles] = useState<Role[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const [showModal, setShowModal] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [status, setStatus] = useState("active");
  const [hasExistingPermission, setHasExistingPermission] = useState(false);

  // const [permissions, setPermissions] = useState<RolePermissionType[]>(() => {
  //   const flatItems = MENU_ITEMS;
  //   return flatItems.map((item, index) => ({
  //     id: index + 1,
  //     pageKey: item.key,
  //     pageName: item.label,
  //     pageUrl: item.url,
  //     add: false,
  //     view: false,
  //     edit: false,
  //     delete: false,
  //   }));
  // });

  const [permissions, setPermissions] = useState<RolePermissionType[]>(() => {
    const allItems = [...ADMIN_MENU_ITEMS, ...USER_MENU_ITEMS];
    const uniqueItems = allItems.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.key === item.key),
    );
    return uniqueItems.map((item, index) => ({
      id: index + 1,
      pageKey: item.key,
      pageName: item.label,
      pageUrl: item.url,
      add: false,
      view: false,
      edit: false,
      delete: false,
    }));
  });
  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      loadRolePermissions(parseInt(selectedRole));
    }
  }, [selectedRole]);

  const loadRoles = async () => {
    try {
      const res = await getRoles();
      console.log("Roles response:", res);
      const formattedRoles = res.map((r: RoleMaster) => ({
        id: r.id,
        name: r.roleName,
      }));
      setRoles(formattedRoles);
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  };

  const loadRolePermissions = async (roleId: number) => {
    try {
      const response = await getRolePermissions(roleId);
      const backendPermissions = response.data?.data || [];

      console.log("Backend permissions:", backendPermissions);

      setHasExistingPermission(backendPermissions.length > 0);

      setPermissions((prev) =>
        prev.map((perm) => {
          const backendPerm = backendPermissions.find(
            (bp: any) =>
              bp.page_url?.toLowerCase() === perm.pageKey.toLowerCase(),
          );

          if (backendPerm) {
            return {
              ...perm,
              add: backendPerm.add == 1,
              view: backendPerm.view == 1,
              edit: backendPerm.edit == 1,
              delete: backendPerm.delete == 1,
            };
          }

          return {
            ...perm,
            add: false,
            view: false,
            edit: false,
            delete: false,
          };
        }),
      );
    } catch (error) {
      console.error("Error loading permissions:", error);
    }
  };
  const handleAddRole = async () => {
    if (!roleName) {
      alert("Enter role name");
      return;
    }

    const payload: RoleMaster = {
      roleName,
      status,
    };

    try {
      await dispatch<any>(addRole(payload));
      await loadRoles();
      alert("Role added successfully");

      // reset
      setRoleName("");
      setStatus("active");
      setShowModal(false);
    } catch (error) {
      console.error(error);
      alert("Error adding role");
    }
  };
  const handlePermissionChange = (
    pageKey: string,
    action: keyof RolePermissionType,
    checked: boolean,
  ) => {
    setPermissions((prev) =>
      prev.map((perm) =>
        perm.pageKey === pageKey ? { ...perm, [action]: checked } : perm,
      ),
    );
  };

  const handleSelectAllActions = (pageKey: string, checked: boolean) => {
    setPermissions((prev) =>
      prev.map((perm) =>
        perm.pageKey === pageKey
          ? {
              ...perm,
              add: checked,
              view: checked,
              edit: checked,
              delete: checked,
            }
          : perm,
      ),
    );
  };

  const handleClear = () => {
    setPermissions((prev) =>
      prev.map((perm) => ({
        ...perm,
        add: false,
        view: false,
        edit: false,
        delete: false,
      })),
    );
    setSelectedRole("");
  };

  const handleSave = async () => {
    if (!selectedRole) {
      alert("Select role");
      return;
    }

    const payload = {
      roleId: parseInt(selectedRole),
      permissions: permissions
        .filter((p) => p.add || p.view || p.edit || p.delete)
        .map((perm) => ({
          pageTitle: perm.pageName,
          pageUrl: perm.pageKey,
          add: perm.add ? 1 : 0,
          view: perm.view ? 1 : 0,
          edit: perm.edit ? 1 : 0,
          delete: perm.delete ? 1 : 0,
        })),
    };

    console.log("Sending payload:", payload);

    try {
      if (hasExistingPermission) {
        await updateRolePermissions(payload);
        alert("Permissions updated successfully");
      } else {
        await saveRolePermissions(payload);
        alert("Permissions added successfully");
      }

      handleClear();
    } catch (error) {
      console.error(error);
      alert("Error saving permissions");
    }
  };
  const isAllSelectedForPage = (pageKey: string) => {
    const perm = permissions.find((p) => p.pageKey === pageKey);
    return perm ? perm.add && perm.view && perm.edit && perm.delete : false;
  };

  return (
    <Main>
      <PageContainer title="Role-Permission">
        <div className="role-permission-manager">
          <div className="header">
            <h2>Manage Permissions</h2>
            <div className="actions">
              <button
                onClick={handleClear}
                className="btn btn-clear"
                disabled={isLoading}
                type="button"
              >
                Clear
              </button>
              <button
                onClick={() => {
                  setShowModal(true);
                }}
                className="btn btn-primary"
                type="button"
              >
                Add Role
              </button>
            </div>
          </div>
          <div className="role-selector">
            <label htmlFor="role">Roles</label>
            <select
              id="role"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              disabled={isLoading}
            >
              <option value="">Select role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id.toString()}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          {/* ✅ Permission Table */}
          {selectedRole && (
            <div className="permission-table-container">
              <table className="permission-table">
                <thead>
                  <tr className="header-row">
                    <th className="access-col">Access to</th>
                    <th colSpan={4} className="actions-col">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((perm) => (
                    <tr key={perm.pageKey} className="permission-row">
                      <td className="page-name">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={isAllSelectedForPage(perm.pageKey)}
                            onChange={(e) =>
                              handleSelectAllActions(
                                perm.pageKey,
                                e.target.checked,
                              )
                            }
                          />
                          {perm.pageName}
                        </label>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={perm.add || false}
                          onChange={(e) =>
                            handlePermissionChange(
                              perm.pageKey,
                              "add",
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={perm.view || false}
                          onChange={(e) =>
                            handlePermissionChange(
                              perm.pageKey,
                              "view",
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={perm.edit || false}
                          onChange={(e) =>
                            handlePermissionChange(
                              perm.pageKey,
                              "edit",
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={perm.delete || false}
                          onChange={(e) =>
                            handlePermissionChange(
                              perm.pageKey,
                              "delete",
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: "20px", textAlign: "right" }}>
            <button
              onClick={handleSave}
              className="btn btn-success"
              type="button"
            >
              Save Permissions
            </button>
          </div>
        </div>
      </PageContainer>
      {showModal && (
        <Modal
          visible={showModal}
          size="lg"
          onClose={() => setShowModal(false)}
          headerText={"Add Role"}
        >
          <Row>
            <Col md={6}>
              <div style={{ marginBottom: "10px" }}>
                <label>Role Name</label>
                <input
                  type="text"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  style={{ width: "100%", padding: "8px", marginTop: "5px" }}
                />
              </div>
            </Col>
            <Col md={6}>
              <div style={{ marginBottom: "10px" }}>
                <label>Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={{ width: "100%", padding: "8px", marginTop: "5px" }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-6 me-3 mt-3">
            <button
              className="btn btn-secondary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>

            <button className="btn btn-primary" onClick={handleAddRole}>
              Save
            </button>
          </div>
        </Modal>
      )}
    </Main>
  );
};

export default RolePermissionManager;
